import { useCallback, useEffect, useId, useState } from "react";
import { useSearchParams } from "react-router-dom";

import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { getSearchQuery } from "components/SearchInput/helpers";
import useErrorHandle from "hooks/useErrorHandle";
import useURLParams from "hooks/useURLParams";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";

import { DEPLOYMENTS_LIMIT, DEPLOYMENTS_LIMIT_STORAGE_KEY } from "./constants";
import TemplateDeploymentsEmpty from "./Empty";
import FiltersLayout from "./FiltersLayout";
import TemplateDeploymentsPageLayout from "./PageLayout";
import useTemplates from "./hooks/useDeployments";
import DeploymentsTable from "./Table";

type TemplateVersionsProps = {
  id: string;
};

const TemplateDeployments = ({ id }: TemplateVersionsProps) => {
  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [searchParams] = useSearchParams();

  const {
    initialLoading,
    error,
    deployments,
    loadDeployments,
    endCursor,
    startCursor,
    noResults,
    isEmpty,
    loadingContent,
    predicates,
  } = useTemplates({ id });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
    resetToInitialPage: resetPaginationToInitialPage,
  } = usePagination({
    fetchItems: loadDeployments,
    endCursor,
    startCursor,
    visibleItemsCount: deployments.length,
    initialLimit: DEPLOYMENTS_LIMIT,
    allItemsCount: itemsCount,
    storageKey: DEPLOYMENTS_LIMIT_STORAGE_KEY,
  });

  const handleSortCallback = useCallback(() => {
    resetPaginationToInitialPage();
  }, [resetPaginationToInitialPage]);

  useEffect(() => {
    if (searchParams.size === 0 && page !== 1) {
      goToFirstPage();
    }
  }, [searchParams, page, goToFirstPage]);

  const ErrorContent = useErrorHandle(error);

  const titleId = useId();

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return (
      <TemplateDeploymentsPageLayout titleId={titleId}>
        <PageLayoutSkeleton />
      </TemplateDeploymentsPageLayout>
    );
  }

  if (noResults) {
    return <NotFoundPage />;
  }

  const hasNoResultsWithFiltering = searchInput.length > 0 || predicates.length > 0;

  return (
    <TemplateDeploymentsPageLayout titleId={titleId}>
      <FiltersLayout
        predicates={predicates}
        itemsCount={itemsCount}
        setItemsCount={setItemsCount}
        titleId={titleId}
        id={id}
      >
        {isEmpty && <TemplateDeploymentsEmpty hasNoResults={hasNoResultsWithFiltering} />}

        {deployments.length > 0 && (
          <>
            <DeploymentsTable
              deployments={deployments}
              loading={loadingContent}
              rowsPerPage={rowsPerPage}
              sortCallback={handleSortCallback}
            />

            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={itemsCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </FiltersLayout>
    </TemplateDeploymentsPageLayout>
  );
};

export default TemplateDeployments;
